import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Select, Spin, message } from 'antd';
import { 
  DesktopOutlined, 
  WifiOutlined, 
  UserOutlined
} from '@ant-design/icons';
import { 
  PieChart, 
  Pie, 
  Cell, 
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip
} from 'recharts';
import { statsAPI } from '@/services/api/stats';
import type { DeviceOverviewStat, ModelDistributionItem, RegionDistributionItem } from '@/types/api';

interface DeviceModel {
  name: string;
  value: number;
  color: string;
  [key: string]: any;
}

interface RegionData {
  region: string;
  count: number;
}

// 预定义颜色数组，用于机型分布饼图
const MODEL_COLORS = ['#1890ff', '#722ed1', '#eb2f96', '#fa8c16', '#52c41a', '#13c2c2', '#f5222d', '#8c8c8c'];

// 将前端时间范围转换为后端格式
const mapTimeRangeToBackend = (timeRange: string): 'today' | 'last_7_days' | 'last_30_days' | 'last_90_days' => {
  const mapping: Record<string, 'today' | 'last_7_days' | 'last_30_days' | 'last_90_days'> = {
    'today': 'today',
    'last7days': 'last_7_days',
    'last30days': 'last_30_days',
    'last90days': 'last_90_days',
  };
  return mapping[timeRange] || 'last_7_days';
};

const DeviceStatistics: React.FC = () => {
  const [timeRange, setTimeRange] = useState('last7days');
  const [loading, setLoading] = useState(true);
  const [deviceOverview, setDeviceOverview] = useState<DeviceOverviewStat | null>(null);
  const [deviceModels, setDeviceModels] = useState<DeviceModel[]>([]);
  const [regionData, setRegionData] = useState<RegionData[]>([]);

  // 时间范围选项
  const timeRangeOptions = [
    { label: '今天', value: 'today' },
    { label: '最近7天', value: 'last7days' },
    { label: '最近30天', value: 'last30days' },
    { label: '最近90天', value: 'last90days' },
  ];

  // 获取设备统计数据
  const fetchDeviceStatistics = async () => {
    try {
      setLoading(true);
      const backendTimeRange = mapTimeRangeToBackend(timeRange);

      // 并行获取所有数据
      const [overview, modelDist, regionDist] = await Promise.all([
        statsAPI.getDeviceOverview({ time_range: backendTimeRange }),
        statsAPI.getModelDistribution(),
        statsAPI.getRegionTop10(),
      ]);

      // 设置设备概况
      setDeviceOverview(overview);

      // 转换机型分布数据，添加颜色
      const modelData: DeviceModel[] = modelDist.items.map((item: ModelDistributionItem, index: number) => ({
        name: item.modelName,
        value: Number(item.percent.toFixed(1)),
        color: MODEL_COLORS[index % MODEL_COLORS.length],
      }));
      setDeviceModels(modelData);

      // 转换地区分布数据
      const regionDataList: RegionData[] = regionDist.items.map((item: RegionDistributionItem) => ({
        region: item.region,
        count: item.count,
      }));
      setRegionData(regionDataList);
    } catch (error: any) {
      console.error('获取设备统计数据失败:', error);
      message.error(error?.message || '获取设备统计数据失败');
    } finally {
      setLoading(false);
    }
  };

  // 当时间范围改变时重新获取数据
  useEffect(() => {
    fetchDeviceStatistics();
  }, [timeRange]);


  // 饼图专用Tooltip
  const PieTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      return (
        <div 
          className="p-3 rounded-lg shadow-lg"
          style={{
            backgroundColor: 'var(--bg-tertiary)',
            border: '1px solid var(--border-primary)',
            color: 'var(--text-primary)'
          }}
        >
          <p style={{ color: 'var(--text-primary)', margin: 0 }}>
            {data.name}: {data.value}%
          </p>
        </div>
      );
    }
    return null;
  };

  // 计算地区分布的最大值，用于设置Y轴范围
  const maxRegionCount = regionData.length > 0 
    ? Math.max(...regionData.map(item => item.count))
    : 0;
  const yAxisMax = maxRegionCount > 0 ? Math.ceil(maxRegionCount * 1.1) : 1000;

  return (
    <div className="space-y-6">
      {/* 页面标题和时间选择器 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>设备统计</h1>
          <p style={{ color: 'var(--text-secondary)' }}>查看设备分布和活跃度统计分析</p>
        </div>
        <div className="flex items-center space-x-4">
          <span style={{ color: 'var(--text-secondary)' }}>时间范围:</span>
          <Select
            value={timeRange}
            onChange={setTimeRange}
            style={{ width: 120 }}
            loading={loading}
          >
            {timeRangeOptions.map(option => (
              <Select.Option key={option.value} value={option.value}>
                {option.label}
              </Select.Option>
            ))}
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center" style={{ minHeight: '400px' }}>
          <Spin size="large" />
        </div>
      ) : (
        <Row gutter={[24, 24]}>
          {/* 设备概况 */}
          <Col xs={24} lg={12}>
            <Card 
              title="设备概况" 
              className="h-full"
              style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border-primary)' }}
              styles={{
                header: {
                  background: 'var(--bg-tertiary)',
                  borderBottom: '1px solid var(--border-primary)',
                  color: 'var(--text-primary)'
                }
              }}
            >
              <p className="mb-6" style={{ color: 'var(--text-secondary)' }}>设备总览和活跃度统计</p>
              
              {deviceOverview && (
                <Row gutter={[16, 16]}>
                  <Col span={24}>
                    <div className="p-4 rounded-lg mb-4" style={{ background: 'var(--bg-tertiary)' }}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center">
                          <DesktopOutlined className="text-xl mr-3" style={{ color: 'var(--color-primary)' }} />
                          <span className="font-medium text-lg" style={{ color: 'var(--text-primary)' }}>设备总数</span>
                        </div>
                        <div className="text-right">
                          <div className="text-sm" style={{ color: 'var(--color-success)' }}>+{deviceOverview.newDevicesThisWeek} 本周新增</div>
                        </div>
                      </div>
                      <div className="font-bold text-3xl" style={{ color: 'var(--text-primary)' }}>{deviceOverview.totalDevices.toLocaleString()}</div>
                    </div>
                  </Col>

                  <Col span={12}>
                    <div className="p-4 rounded-lg" style={{ background: 'var(--bg-tertiary)' }}>
                      <div className="flex items-center mb-3">
                        <WifiOutlined className="text-xl mr-3" style={{ color: 'var(--color-success)' }} />
                        <span className="font-medium" style={{ color: 'var(--text-primary)' }}>在线设备</span>
                      </div>
                      <div className="font-bold text-2xl mb-1" style={{ color: 'var(--text-primary)' }}>{deviceOverview.onlineDevices.toLocaleString()}</div>
                      <div className="text-sm" style={{ color: 'var(--color-success)' }}>{deviceOverview.onlineRate.toFixed(1)}% 在线率</div>
                    </div>
                  </Col>

                  <Col span={12}>
                    <div className="p-4 rounded-lg" style={{ background: 'var(--bg-tertiary)' }}>
                      <div className="flex items-center mb-3">
                        <UserOutlined className="text-xl mr-3" style={{ color: 'var(--color-primary)' }} />
                        <span className="font-medium" style={{ color: 'var(--text-primary)' }}>活跃设备</span>
                      </div>
                      <div className="font-bold text-2xl mb-1" style={{ color: 'var(--text-primary)' }}>{deviceOverview.activeDevices.toLocaleString()}</div>
                      <div className="text-sm" style={{ color: 'var(--color-success)' }}>{deviceOverview.activeRate.toFixed(1)}% 活跃率</div>
                    </div>
                  </Col>
                </Row>
              )}
            </Card>
          </Col>

          {/* 机型分布 */}
          <Col xs={24} lg={12}>
            <Card 
              title="机型分布" 
              className="h-full"
              style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border-primary)' }}
              styles={{
                header: {
                  background: 'var(--bg-tertiary)',
                  borderBottom: '1px solid var(--border-primary)',
                  color: 'var(--text-primary)'
                }
              }}
            >
              <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>各机型设备数量占比</p>
              
              {deviceModels.length > 0 ? (
                <div style={{ height: '300px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={deviceModels}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={120}
                        paddingAngle={5}
                        dataKey="value"
                        nameKey="name"
                        label={({ name, value }) => `${name} ${value}%`}
                        labelLine={true}
                      >
                        {deviceModels.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip content={<PieTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="flex justify-center items-center" style={{ height: '300px', color: 'var(--text-secondary)' }}>
                  暂无数据
                </div>
              )}
            </Card>
          </Col>

          {/* 地区分布 TOP 10 */}
          <Col span={24}>
            <Card 
              title="地区分布 TOP 10" 
              style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border-primary)' }}
              styles={{
                header: {
                  background: 'var(--bg-tertiary)',
                  borderBottom: '1px solid var(--border-primary)',
                  color: 'var(--text-primary)'
                }
              }}
            >
              <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>按设备数量排序的地区分布</p>
              
              {regionData.length > 0 ? (
                <div style={{ height: '400px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart 
                      data={regionData} 
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border-secondary)" />
                      <XAxis 
                        dataKey="region" 
                        stroke="var(--text-secondary)"
                        fontSize={12}
                        angle={-45}
                        textAnchor="end"
                        height={80}
                      />
                      <YAxis 
                        stroke="var(--text-secondary)"
                        fontSize={12}
                        domain={[0, yAxisMax]}
                      />
                      <Tooltip 
                        formatter={(value) => [`${value ?? 0}`, '设备数']}
                        labelFormatter={(label: string) => `${label}`}
                        contentStyle={{
                          backgroundColor: 'var(--bg-secondary)',
                          border: '1px solid var(--border-primary)',
                          borderRadius: '6px',
                          color: 'var(--text-primary)',
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                        }}
                      />
                      <Bar 
                        dataKey="count" 
                        fill="#722ed1"
                        name="设备数"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="flex justify-center items-center" style={{ height: '400px', color: 'var(--text-secondary)' }}>
                  暂无数据
                </div>
              )}
            </Card>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default DeviceStatistics;
